import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { Loader } from 'Src/components/Loader/Loader';
import { StatusModal } from 'Src/components/StatusModal/StatusModal';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { ResizablePanel } from 'Src/components/ResizablePanel/ResizablePanel';
import {
  getInboxTournamentConfirmationRequests,
  getInboxTournamentConfirmationRequestsCount,
  rejectConfirmationRequest,
  acceptConfirmationRequest
} from 'Src/helpers/service/admin/messages';
import { transformUIFilters, getDeadlineIndicator } from 'Src/helpers/message/message';
import { GenericSummary } from 'Src/components/GenericSummary/GenericSummary';
import { TournamentConfirmationRequestSummary } from 'Src/components/MessageSummary/TournamentConfirmationRequestSummary';
import { MessageAdditionalSummary } from 'Src/components/MessageSummary/MessageAdditionalSummary';
import { MESSAGE_TABS } from 'Src/consts/message';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { ConfirmationRequestForm } from '../Messages/ConfirmationRequestForm/ConfirmationRequestForm';
import { Button } from 'Src/components/Button/Button';
import { FormSubmission } from 'Src/types/formSchema';
import './Messages.scss';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Tournament name',
    field: 'tournamentName',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: message => (message.tournamentData ? message.tournamentData.name : '')
  },
  {
    text: 'Date',
    field: 'tournamentStartDate',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (message: any): JSX.Element => {
      let content: React.ReactNode = <span>—</span>;

      if (message.tournamentData && message.tournamentData.startTime) {
        const startDate = new Date(message.tournamentData.startTime);
        const now = new Date();
        const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
        const timeDiff = startDate.getTime() - now.getTime();
        const isWithinWeek = timeDiff > 0 && timeDiff <= oneWeekMs;

        content = (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{startDate.toLocaleDateString()}</span>

            {isWithinWeek ? (
              <span
                className="badge badge-light border"
                title="This tournament starts within the next 7 days."
                style={{ fontWeight: 600 }}
              >
                Starts soon
              </span>
            ) : null}
          </div>
        );
      }

      return (
        <td className="start-date-cell" style={{ width: '180px' }}>
          {content}
        </td>
      );
    }
  },
  {
    text: 'Deadline',
    field: 'deadlineForAnswers',
    isSort: false,
    type: FILTER_TYPE.NONE,
    cell: (message: any): JSX.Element => {
      const now = new Date();

      const deadlineRaw = message.deadlineForAnswers;
      const hasDeadline = Boolean(deadlineRaw);

      const pad2 = (n: number) => {
        const s = String(n);
        return s.length >= 2 ? s : `0${s}`;
      };

      const formatDateTime = (d: Date) => {
        return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(
          d.getMinutes()
        )}`;
      };

      let label: React.ReactNode = <span>—</span>;
      let icon: React.ReactNode = null;

      if (hasDeadline) {
        const deadline = new Date(deadlineRaw);
        const msLeft = deadline.getTime() - now.getTime();

        const isSameDay =
          deadline.getFullYear() === now.getFullYear() &&
          deadline.getMonth() === now.getMonth() &&
          deadline.getDate() === now.getDate();

        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);

        const isTomorrow =
          deadline.getFullYear() === tomorrow.getFullYear() &&
          deadline.getMonth() === tomorrow.getMonth() &&
          deadline.getDate() === tomorrow.getDate();

        let deadlineBadgeText = 'Reply due';

        if (msLeft < 0) {
          deadlineBadgeText = 'Passed';
        } else if (isSameDay) {
          deadlineBadgeText = 'Today';
        } else if (isTomorrow) {
          deadlineBadgeText = 'Tomorrow';
        } else if (msLeft <= 72 * 3600 * 1000) {
          deadlineBadgeText = 'Soon';
        }

        const formatRemaining = (ms: number) => {
          const totalMinutes = Math.max(0, Math.floor(ms / 60000));
          const h = Math.floor(totalMinutes / 60);
          const m = totalMinutes % 60;

          if (h <= 0) return `${m}m left`;
          return `${h}h ${m}m left`;
        };

        let color = '#6c757d';
        if (msLeft > 72 * 3600 * 1000) color = '#2f8f3a';
        else if (msLeft > 24 * 3600 * 1000) color = '#d39e00';
        else if (msLeft >= 0) color = '#dc3545';
        else color = '#6c757d';

        const showCountdown = msLeft >= 0 && msLeft < 2 * 3600 * 1000;

        const countdown = showCountdown ? (
          <span style={{ marginLeft: '6px', fontWeight: 600 }}>({formatRemaining(msLeft)})</span>
        ) : null;

        icon = (
          <span
            className="badge badge-light border"
            title={msLeft >= 0 ? 'Response deadline' : 'Deadline passed'}
            style={{
              marginLeft: 6,
              color,
              fontWeight: 600
            }}
          >
            {deadlineBadgeText}
          </span>
        );

        label = (
          <span>
            {formatDateTime(deadline)}
            {icon}
            {countdown}
          </span>
        );
      }

      return (
        <td className="deadline-cell" style={{ width: '220px' }}>
          {label}
        </td>
      );
    }
  }
];

interface State {
  items: any[];
  selectedItems: any[];
  currentPage: number;
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  currentTabIndex: number;
  currentMessage: any | null;
  isConfirmationRequestModalOpen: boolean;
  isDeclineConfirmationModalOpen: boolean;
  isSuccessModalOpen: boolean;
  successModalTitle: string;
  successModalMessage: string;
  successModalLines: string[];
  successModalStatus: 'success' | 'info' | 'warning' | 'error';
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

function tournamentNameSearchFilter(filter: any): string {
  return 'filter=' + encodeURIComponent(JSON.stringify(filter));
}

export class TournamentConfirmationMessagesInbox extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      selectedItems: [],
      currentPage: FIRST_PAGE,
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      currentTabIndex: 0,
      currentMessage: null,
      isConfirmationRequestModalOpen: false,
      isDeclineConfirmationModalOpen: false,
      isSuccessModalOpen: false,
      successModalTitle: '',
      successModalMessage: '',
      successModalLines: [],
      successModalStatus: 'success'
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;
    const search = parse(history.location.search);
    const page = search.page ? Number(search.page) : FIRST_PAGE;
    const uiFilters = getFilters2(COLUMNS, search);
    const serverFilters = transformUIFilters(uiFilters);
    const whereClause = serverFilters.where || serverFilters;
    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';
    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }
    const order = sortColumnsName && sortDirection ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, whereClause, order);
    const getItemsPromise = getInboxTournamentConfirmationRequests(user, serverQueryFilter);
    const getItemsCountPromise = getInboxTournamentConfirmationRequestsCount(user, whereClause);
    return BPromise.all([getItemsCountPromise, getItemsPromise]).then(([count, items]) => {
      this.setState({
        currentPage: page,
        sortDirection,
        sortColumnsName,
        isShowFilter: isFilterExist2(uiFilters),
        isDataFiltered: isFilterExist2(uiFilters),
        filters: uiFilters,
        items,
        total: count,
        basePath: history.location.pathname,
        isLoading: false,
        selectedItems: [],
        currentMessage: null
      });
      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;
    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }
    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }
    if (isFilterExist2(filters)) {
      search.push(tournamentNameSearchFilter(transformUIFilters(filters)));
    }
    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const clickedItem = items[index];
    let newSelected = [...selectedItems];
    const existingIndex = newSelected.findIndex(item => item.id === clickedItem.id);
    if (existingIndex !== -1) {
      newSelected.splice(existingIndex, 1);
    } else {
      newSelected.push(clickedItem);
    }
    this.setState({
      selectedItems: newSelected,
      currentMessage: newSelected.length === 1 ? newSelected[0] : null
    });
  };

  onRowViewClick = (message: any): void => {
    this.setState({
      selectedItems: [message],
      currentMessage: message,
      isConfirmationRequestModalOpen: true
    });
  };

  onRowDeclineClick = (message: any): void => {
    this.setState({
      selectedItems: [message],
      currentMessage: message,
      isDeclineConfirmationModalOpen: true
    });
  };

  renderRowActions = (message: any): JSX.Element => {
    const organisersEmail = message.tournamentData?.organisersEmail;
    const schoolEmail = message.schoolData?.email;
    const emailToUse = organisersEmail && organisersEmail.trim() ? organisersEmail : schoolEmail;
    const tournamentName = message.tournamentData?.name || '';

    return (
      <div className="d-flex align-items-center justify-content-end flex-wrap" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={e => {
            e.stopPropagation();
            this.onRowViewClick(message);
          }}
        >
          Review &amp; Accept
        </button>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={e => {
            e.stopPropagation();
            this.onRowDeclineClick(message);
          }}
        >
          Decline
        </button>

        {emailToUse ? (
          <a
            href={`mailto:${emailToUse}?subject=${encodeURIComponent(tournamentName)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
          >
            <button type="button" className="btn btn-sm btn-outline-secondary">
              Contact organiser
            </button>
          </a>
        ) : null}
      </div>
    );
  };

  onSelectAllClick = (): void => {
    this.setState({
      selectedItems: [...this.state.items],
      currentMessage: this.state.items.length === 1 ? this.state.items[0] : null,
      isSelectAllChecked: true
    });
  };

  onSelectAllOnPageClick = (): void => {
    this.onSelectAllClick();
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      currentMessage: null
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);
    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }
    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };
    if (filterField !== 'tournamentStartDate') {
      if (typeof options !== 'undefined') {
        switch (options) {
          case DATE_INTERVAL.FROM:
            nextFilters = {
              ...nextFilters,
              [filterField]: { ...currentFilterField, from: filterValue }
            };
            break;
          case DATE_INTERVAL.TO:
            nextFilters = {
              ...nextFilters,
              [filterField]: { ...currentFilterField, to: filterValue }
            };
            break;
        }
      } else {
        nextFilters = { ...nextFilters, [filterField]: filterValue };
      }
      this.setState({ filters: nextFilters });
    }
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let searchParams = [];
    if (isSortExist(sortDirection, sortColumnsName)) {
      searchParams.push(getSearchOrder(sortDirection, sortColumnsName));
    }
    if (isFilterExist2(filters)) {
      searchParams.push(getSearchFilter(filters));
    }
    this.setState({
      selectedItems: [],
      currentMessage: null,
      isSelectAllChecked: false
    });
    this.props.history.push({
      pathname: this.state.basePath,
      search: searchParams.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      filters: {},
      selectedItems: [],
      currentMessage: null,
      isSelectAllChecked: false
    });
    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onTabClick = (event, index: number): void => {
    event.preventDefault();
    this.setState({ currentTabIndex: index });
  };

  getSummaryContent(): React.ReactNode {
    const { currentMessage, currentTabIndex } = this.state;
    if (!currentMessage) {
      return <div className="font-weight-bold">Select a message to view details</div>;
    }
    if (currentTabIndex === 0) {
      return <TournamentConfirmationRequestSummary message={currentMessage} />;
    } else if (currentTabIndex === 1) {
      return <MessageAdditionalSummary message={currentMessage} />;
    }
    return null;
  }

  renderMessageTable(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      filters,
      selectedItems,
      currentPage,
      total,
      isSelectAllChecked
    } = this.state;
    const columns: ColumnDefinition[] = [
      ...COLUMNS,
      {
        text: '',
        field: 'actions',
        isSort: false,
        type: FILTER_TYPE.NONE,
        align: 'center',
        cell: (message: any): JSX.Element => <td className="action-cell">{this.renderRowActions(message)}</td>
      }
    ];
    return (
      <Grid2
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isFilterExist2(filters)}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={columns}
        options={{}}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        actionItems={[]}
      />
    );
  }

  onViewConfirmationRequestButtonClick = (): void => {
    this.setState({
      isConfirmationRequestModalOpen: true
    });
  };

  onCloseConfirmationRequestClick = (): void => {
    this.setState({
      isConfirmationRequestModalOpen: false
    });
  };

  onSubmitConfirmationRequest = (data: any[], submission?: FormSubmission): void => {
    const { user } = this.props;
    const { currentMessage } = this.state;
    if (!currentMessage) {
      return;
    }

    acceptConfirmationRequest(user, currentMessage.id, { fields: data, ...submission })
      .then(() => {
        this.setState({
          isConfirmationRequestModalOpen: false,
          selectedItems: [],
          currentMessage: null,
          isSuccessModalOpen: true,
          successModalTitle: 'Confirmation sent successfully',
          successModalMessage: 'Your answers have been sent to the tournament organisers.',
          successModalLines: [
            'If they need any additional information, they will contact your school directly.',
            'You can review what you submitted later in the Confirmation Requests archive.'
          ]
        });
        this.setItems();
      });
  };

  onProceedDecline = (): void => {
    const { user } = this.props;
    const { currentMessage } = this.state;
    if (!currentMessage) return;

    rejectConfirmationRequest(user, currentMessage.id).then(() => {
      this.setState({
        isDeclineConfirmationModalOpen: false,
        currentMessage: null,
        selectedItems: [],
        isSuccessModalOpen: true,
        successModalStatus: 'warning',
        successModalTitle: 'Tournament request declined',
        successModalMessage: 'The tournament organisers have been notified that your school declined this request.',
        successModalLines: [
          'If your decision changes later, please contact the organisers directly.',
          'You can review this request later in the Confirmation Requests archive.'
        ]
      });
      this.setItems();
    });
  };

  onCloseDeclineConfirmationModal = (): void => {
    this.setState({ isDeclineConfirmationModalOpen: false });
  };

  renderConfirmationRequestModal = (): React.ReactNode => {
    const { user } = this.props;
    const { isConfirmationRequestModalOpen, currentMessage } = this.state;
    return (
      <SimpleModal
        customClass=""
        isOpen={isConfirmationRequestModalOpen}
        onCloseClick={this.onCloseConfirmationRequestClick}
        showFooter={false}
      >
        <ConfirmationRequestForm
          user={user}
          message={currentMessage}
          isEditable={true}
          onAcceptClick={this.onSubmitConfirmationRequest}
          onCloseClick={this.onCloseConfirmationRequestClick}
        />
      </SimpleModal>
    );
  };

  renderDeclineConfirmationModal = (): React.ReactNode => {
    const { currentMessage, isDeclineConfirmationModalOpen } = this.state;
    if (!currentMessage) return null;

    const tournamentName = String(propz.get(currentMessage, ['tournamentData', 'name'], 'Tournament'));
    const schoolName = String(propz.get(currentMessage, ['schoolData', 'name'], 'Your school'));
    const startTimeRaw = propz.get(currentMessage, ['tournamentData', 'startTime'], null);
    const endTimeRaw = propz.get(currentMessage, ['tournamentData', 'endTime'], null);
    const venueText = String(propz.get(currentMessage, ['tournamentData', 'venue', 'postcode'], '') || '').trim() || '';

    const formatTournamentWindow = (startRaw: any, endRaw: any) => {
      if (!startRaw) return '';

      const start = new Date(startRaw);
      if (Number.isNaN(start.getTime())) return '';

      const pad2 = (n: number) => (String(n).length >= 2 ? String(n) : `0${n}`);
      const formatDate = (d: Date) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
      const formatTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

      if (!endRaw) return `${formatDate(start)} ${formatTime(start)}`;

      const end = new Date(endRaw);
      if (Number.isNaN(end.getTime())) return `${formatDate(start)} ${formatTime(start)}`;

      const isSameDay =
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate();

      if (isSameDay) {
        return `${formatDate(start)} ${formatTime(start)} - ${formatTime(end)}`;
      }

      return `${formatDate(start)} ${formatTime(start)} - ${formatDate(end)} ${formatTime(end)}`;
    };

    const tournamentWindowText = formatTournamentWindow(startTimeRaw, endTimeRaw);

    return (
      <SimpleModal
        isOpen={isDeclineConfirmationModalOpen}
        onCloseClick={this.onCloseDeclineConfirmationModal}
        title="Decline tournament participation"
        showFooter={false}
      >
        <div className="programmeBookingModal">
          <div className="programmeBookingModalHero mb-4">
            <div className="programmeBookingModalHeroTop">
              <div>
                <div className="programmeBookingModalEyebrow">Tournament confirmation request</div>
                <div className="programmeBookingModalClubName">{tournamentName}</div>
              </div>

              <div className="programmeBookingModalBadges">
                {schoolName ? <span className="badge badge-light border mr-2 mb-2">{schoolName}</span> : null}
              </div>
            </div>

            <div className="programmeBookingModalMeta mt-2">
              {tournamentWindowText ? <span>Tournament window: {tournamentWindowText}</span> : null}
              {venueText ? <span>Venue: {venueText}</span> : null}
            </div>

            <div className="programmeBookingModalInviteNote mt-3">
              Declining will notify the organisers that your school is not taking part in this tournament.
            </div>
          </div>

          <div className="alert alert-warning mb-3">
            <b>Please confirm before continuing.</b>
            <div className="mt-2">
              If you proceed, your school will be treated as not participating in this event and removed from the
              participant list.
            </div>
          </div>

          <div className="small text-muted mb-4">
            If this was selected by mistake, choose <b>Cancel</b> and return to the request instead.
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-outline-secondary mr-2"
              onClick={this.onCloseDeclineConfirmationModal}
            >
              Cancel
            </button>

            <button type="button" className="btn btn-danger" onClick={this.onProceedDecline}>
              Decline
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  };

  renderSuccessModal = (): React.ReactNode => {
    const {
      isSuccessModalOpen,
      successModalStatus,
      successModalTitle,
      successModalMessage,
      successModalLines
    } = this.state;

    return (
      <StatusModal
        isOpen={isSuccessModalOpen}
        status={successModalStatus}
        title={successModalTitle}
        message={successModalMessage}
        lines={successModalLines}
        onClose={() =>
          this.setState({
            isSuccessModalOpen: false,
            successModalStatus: 'success',
            successModalTitle: '',
            successModalMessage: '',
            successModalLines: []
          })
        }
      />
    );
  };

  renderSummary(): React.ReactNode {
    const { currentTabIndex } = this.state;
    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={MESSAGE_TABS} currentTabIndex={currentTabIndex}>
        {this.getSummaryContent()}
      </GenericSummary>
    );
  }

  render() {
    const { isLoading } = this.state;
    if (isLoading) {
      return <Loader />;
    }
    return (
      <div className="mt-3">
        {this.renderConfirmationRequestModal()}
        {this.renderDeclineConfirmationModal()}
        {this.renderSuccessModal()}
        <div className="row">
          <div className="col-md-12">
            <ResizablePanel>
              {this.renderMessageTable()}
              {this.renderSummary()}
            </ResizablePanel>
          </div>
        </div>
      </div>
    );
  }
}

